import { queryClient } from "@/api/constants"
import { fetchWithNetworkBehavior } from "@/api/fetch-with-network-behavior"
import { store } from "@/store/store"
import { atom } from "jotai"
import { BlobReader, ZipReader } from "@zip.js/zip.js"
import { setOpfsPathSegments } from "@/utils/opfs-path"
import { pixiesetAssetBaseURIs } from "@/utils/pixieset-urls"

const SAMPLE_PROJECT_URL = `${pixiesetAssetBaseURIs.filesPe}/pn/samples/sample-project.zip`
const SAMPLE_PROJECT_BASE_SEGMENTS = ["polarr", "sample-projects"]
const TEMP_ARCHIVE_PREFIX = ".download-sample-project"

export interface SampleProjectProgress {
	stage: "download" | "extract"
	current: number
	total?: number
	entryName?: string
}

export interface SampleProjectProgressState extends SampleProjectProgress {
	percent: number
}

export interface EnsureSampleProjectOptions {
	signal?: AbortSignal
	onProgress?: (progress: SampleProjectProgress) => void
}

export type SampleProjectStatus = "idle" | "loading" | "ready" | "error"

export const sampleProjectStatusAtom = atom<SampleProjectStatus>("idle")
export const sampleProjectErrorAtom = atom<Error | null>(null)
export const sampleProjectProgressAtom = atom<SampleProjectProgressState | null>(null)

export const getSampleProjectRootPath = (projectId: string) => [
	...SAMPLE_PROJECT_BASE_SEGMENTS,
	projectId,
]

type CreateSyncAccessHandle = () => OPFSSyncAccessHandle | Promise<OPFSSyncAccessHandle>

interface FileWriter {
	write: (chunk: Uint8Array) => Promise<void>
	close: () => Promise<void>
	abort: () => Promise<void>
}

interface OPFSSyncAccessHandle {
	write: (buffer: Uint8Array, options?: { at?: number }) => number | Promise<number>
	truncate?: (size: number) => void | Promise<void>
	flush?: () => void | Promise<void>
	close: () => void | Promise<void>
}

interface FilePathOptions {
	root: FileSystemDirectoryHandle
	relativePath: string[]
	basePath?: readonly string[]
}

type WorkerCommandType = "open" | "write" | "close" | "abort"

interface WorkerSuccessMessage {
	requestId: number
	status: "ok"
	writerId?: number
}

interface WorkerErrorMessage {
	requestId: number
	status: "error"
	error: {
		name: string
		message: string
	}
}

type WorkerResponseMessage = WorkerSuccessMessage | WorkerErrorMessage

const cachedHandles = new Map<string, FileSystemDirectoryHandle>()
const inFlightPromises = new Map<string, Promise<FileSystemDirectoryHandle>>()
let writerWorker: Worker | null = null
let writerWorkerRequestId = 0
const writerWorkerPendingRequests = new Map<
	number,
	{
		resolve: (value: unknown) => void
		reject: (error: Error) => void
	}
>()

export async function ensureSampleProjectMounted(
	projectId: string,
	options: EnsureSampleProjectOptions = {},
): Promise<FileSystemDirectoryHandle> {
	const cacheKey = projectId
	const cachedHandle = cachedHandles.get(cacheKey)
	// Return a cached handle immediately to avoid redundant OPFS walks.
	if (cachedHandle) {
		console.info("[SampleProject] Returning cached sample handle", { projectId })
		clearProgress()
		setStatus("ready")
		return cachedHandle
	}

	// Ensure only a single download/extract runs at a time for this project.
	const existingPromise = inFlightPromises.get(cacheKey)
	if (existingPromise) {
		console.info("[SampleProject] Awaiting in-flight mount", { projectId })
		setStatus("loading")
		return existingPromise
	}

	setStatus("loading")
	initializeProgress()
	clearError()

	const mountPromise = mountSampleProject(projectId, options)
		.then((handle) => {
			cachedHandles.set(cacheKey, handle)
			console.info("[SampleProject] Mounted sample project", { projectId, handle })
			clearProgress()
			setStatus("ready")
			clearError()
			void queryClient.invalidateQueries({ queryKey: ["projects"] })
			return handle
		})
		.catch((error) => {
			clearProgress()
			setStatus("error")
			store.set(
				sampleProjectErrorAtom,
				error instanceof Error ? error : new Error(String(error)),
			)
			throw error
		})
		.finally(() => {
			inFlightPromises.delete(cacheKey)
		})

	inFlightPromises.set(cacheKey, mountPromise)
	return mountPromise
}

export const getCachedSampleProjectHandle = (projectId: string) =>
	cachedHandles.get(projectId) ?? null

async function mountSampleProject(
	projectId: string,
	options: EnsureSampleProjectOptions,
): Promise<FileSystemDirectoryHandle> {
	const { signal, onProgress } = options
	const reportProgress = createSampleProjectProgressReporter(onProgress)

	if (!navigator.storage?.getDirectory) {
		throw new Error("OPFS is not available in this environment")
	}

	const opfsRoot = await navigator.storage.getDirectory()
	console.info("[SampleProject] Acquired OPFS root", opfsRoot)
	const rootPath = getSampleProjectRootPath(projectId)

	const existingHandle = await tryGetDirectory(opfsRoot, rootPath)
	if (existingHandle) {
		if (await directoryHasEntries(existingHandle)) {
			console.info("[SampleProject] Using existing sample directory")
			return annotateSampleProjectRoot(existingHandle, rootPath)
		}
		console.warn("[SampleProject] Sample directory empty, rehydrating")
	}

	const projectDirectory = await ensureDirectory(opfsRoot, rootPath)
	console.info("[SampleProject] Created sample directory", projectDirectory)
	await downloadAndExtractArchive({
		targetDirectory: projectDirectory,
		basePath: rootPath,
		signal,
		onProgress: reportProgress,
	})
	return annotateSampleProjectRoot(projectDirectory, rootPath)
}

function annotateSampleProjectRoot(
	handle: FileSystemDirectoryHandle,
	pathSegments: string[],
): FileSystemDirectoryHandle {
	setOpfsPathSegments(handle, pathSegments, { setRelativePath: [] })
	return handle
}

async function tryGetDirectory(
	root: FileSystemDirectoryHandle,
	segments: string[],
): Promise<FileSystemDirectoryHandle | null> {
	let current: FileSystemDirectoryHandle = root
	for (const segment of segments) {
		try {
			current = await current.getDirectoryHandle(segment)
		} catch (error) {
			if (isNotFoundError(error)) {
				return null
			}
			throw error
		}
	}
	return current
}

async function ensureDirectory(
	root: FileSystemDirectoryHandle,
	segments: string[],
): Promise<FileSystemDirectoryHandle> {
	let current: FileSystemDirectoryHandle = root
	for (const segment of segments) {
		current = await current.getDirectoryHandle(segment, { create: true })
	}
	return current
}

async function directoryHasEntries(directory: FileSystemDirectoryHandle): Promise<boolean> {
	for await (const handle of directory.values()) {
		if (handle.name.startsWith(TEMP_ARCHIVE_PREFIX)) {
			continue
		}
		return true
	}
	return false
}

interface DownloadAndExtractParams {
	targetDirectory: FileSystemDirectoryHandle
	basePath: readonly string[]
	signal?: AbortSignal
	onProgress?: (progress: SampleProjectProgress) => void
}

async function downloadAndExtractArchive(params: DownloadAndExtractParams) {
	const { targetDirectory, basePath, signal, onProgress } = params

	const response = await fetchWithNetworkBehavior(
		SAMPLE_PROJECT_URL,
		{ signal },
		{ networkIntent: "active" },
	)
	if (!response.ok) {
		throw new Error(`Failed to download sample project (${response.status})`)
	}
	if (!response.body) {
		throw new Error("Sample project response does not contain a readable body")
	}

	const tempFileName = `${TEMP_ARCHIVE_PREFIX}-${crypto.randomUUID()}.zip`
	const tempFileHandle = await targetDirectory.getFileHandle(tempFileName, { create: true })
	console.info("[SampleProject] Downloading archive to temp file", tempFileName)
	const tempWriter = await createFileWriterForPath(targetDirectory, [tempFileName], basePath)

	let downloadComplete = false
	try {
		await streamResponseToFile({
			body: response.body,
			writer: tempWriter,
			contentLength: parseContentLength(response.headers.get("content-length")),
			signal,
			onProgress: onProgress
				? (current, total) =>
						onProgress({
							stage: "download",
							current,
							total,
						})
				: undefined,
		})
		downloadComplete = true
	} finally {
		if (!downloadComplete) {
			await safeAbortWriter(tempWriter)
		}
	}

	const zipBlob = await tempFileHandle.getFile()
	const zipReader = new ZipReader(new BlobReader(zipBlob))
	console.info("[SampleProject] Extracting archive entries")

	try {
		const entries = await zipReader.getEntries()
		const commonPrefix = findCommonPathPrefix(entries)
		const totalFiles = entries.filter((entry) => !entry.directory).length
		let extractedFiles = 0

		for (const entry of entries) {
			throwIfAborted(signal)

			const segments = entry.filename.split("/").filter(Boolean)
			const relativeSegments = segments.slice(commonPrefix.length)

			const effectiveSegments = relativeSegments.length ? relativeSegments : segments

			if (!effectiveSegments.length) {
				continue
			}

			if (entry.directory) {
				await ensureDirectory(targetDirectory, effectiveSegments)
				continue
			}

			const fileName = effectiveSegments.pop()
			if (!fileName) {
				continue
			}

			const pathSegments = [...effectiveSegments, fileName]
			console.info(
				"[SampleProject] Writing entry",
				entry.filename,
				"->",
				pathSegments.join("/"),
			)

			const { stream, ensureClosed } = await createFileWritableStream({
				root: targetDirectory,
				relativePath: pathSegments,
				basePath,
			})
			try {
				if (!entry.getData) {
					throw new Error(`Zip entry ${entry.filename} does not expose getData()`)
				}
				await entry.getData(stream, signal ? { signal } : undefined)
			} finally {
				await ensureClosed()
			}

			extractedFiles += 1
			onProgress?.({
				stage: "extract",
				current: extractedFiles,
				total: totalFiles || undefined,
				entryName: entry.filename,
			})
		}
	} finally {
		try {
			await zipReader.close()
		} finally {
			await deleteTempArchive(targetDirectory, tempFileName)
		}
	}
}

function parseContentLength(value: string | null): number | undefined {
	if (!value) {
		return undefined
	}
	const parsed = Number(value)
	return Number.isFinite(parsed) ? parsed : undefined
}

async function streamResponseToFile({
	body,
	writer,
	contentLength,
	signal,
	onProgress,
}: {
	body: ReadableStream<Uint8Array>
	writer: FileWriter
	contentLength?: number
	signal?: AbortSignal
	onProgress?: (current: number, total?: number) => void
}) {
	const reader = body.getReader()
	let written = 0

	try {
		let isDone = false
		while (!isDone) {
			throwIfAborted(signal)
			const { done, value } = await reader.read()
			if (done) {
				isDone = true
				continue
			}
			if (!value) {
				continue
			}

			await writer.write(value)
			written += value.byteLength
			onProgress?.(written, contentLength)
		}
		await writer.close()
	} catch (error) {
		await safeAbortWriter(writer)
		throw error
	}
}

function findCommonPathPrefix(entries: { filename: string }[]): string[] {
	const pathSegments = entries
		.map((entry) => entry.filename.split("/").filter(Boolean))
		.filter((segments) => segments.length > 0)

	if (!pathSegments.length) {
		return []
	}

	const first = pathSegments[0]
	if (!first) {
		return []
	}

	let prefix = [...first]
	for (const segments of pathSegments.slice(1)) {
		let commonLength = 0
		while (
			commonLength < prefix.length &&
			commonLength < segments.length &&
			prefix[commonLength] === segments[commonLength]
		) {
			commonLength += 1
		}
		prefix = prefix.slice(0, commonLength)
		if (!prefix.length) {
			break
		}
	}
	return prefix
}

async function deleteTempArchive(
	directory: FileSystemDirectoryHandle,
	fileName: string,
): Promise<void> {
	try {
		await directory.removeEntry(fileName)
	} catch (error) {
		if (!isNotFoundError(error)) {
			console.warn(`Failed to remove temp archive ${fileName}`, error)
		}
	}
}

function isNotFoundError(error: unknown): boolean {
	return error instanceof DOMException && error.name === "NotFoundError"
}

function clearError() {
	store.set(sampleProjectErrorAtom, null)
}

function initializeProgress() {
	store.set(sampleProjectProgressAtom, {
		stage: "download",
		current: 0,
		total: undefined,
		percent: 0,
	})
}

function clearProgress() {
	store.set(sampleProjectProgressAtom, null)
}

function createSampleProjectProgressReporter(
	onProgress?: (progress: SampleProjectProgress) => void,
) {
	return (progress: SampleProjectProgress) => {
		store.set(sampleProjectProgressAtom, {
			...progress,
			percent: calculateSampleProjectProgressPercent(progress),
		})
		onProgress?.(progress)
	}
}

function calculateSampleProjectProgressPercent(progress: SampleProjectProgress) {
	const { stage, current, total } = progress
	const normalizedProgress = total && total > 0 ? Math.min(1, Math.max(0, current / total)) : 0

	if (stage === "download") {
		return Math.round(normalizedProgress * 50)
	}

	return 50 + Math.round(normalizedProgress * 50)
}

async function safeAbortWriter(writer: FileWriter) {
	try {
		await writer.abort()
	} catch (abortError) {
		console.warn("Failed to abort file writer", abortError)
	}
}

function throwIfAborted(signal?: AbortSignal) {
	if (signal?.aborted) {
		throw new DOMException("Aborted", "AbortError")
	}
}

async function createFileWriterForPath(
	root: FileSystemDirectoryHandle,
	segments: string[],
	basePath: readonly string[] = [],
): Promise<FileWriter> {
	if (!Array.isArray(segments) || segments.length === 0) {
		throw new Error("Cannot create file writer without a target path")
	}
	const fileHandle = await getFileHandleFromSegments(root, segments)
	const absoluteBase =
		Array.isArray(basePath) && basePath.length > 0 ? [...basePath] : ([] as string[])
	const absolutePath = absoluteBase.length ? [...absoluteBase, ...segments] : [...segments]
	return createFileWriterFromHandle(fileHandle, absolutePath)
}

async function createFileWriterFromHandle(
	fileHandle: FileSystemFileHandle,
	absolutePathSegments: string[],
): Promise<FileWriter> {
	const hasCreateWritable =
		"createWritable" in fileHandle && typeof fileHandle.createWritable === "function"
	if (hasCreateWritable) {
		const writable = await fileHandle.createWritable()
		let closed = false

		return {
			write: async (chunk) => {
				await writable.write(chunk as unknown as BufferSource)
			},
			close: async () => {
				if (!closed) {
					closed = true
					await writable.close()
				}
			},
			abort: async () => {
				if (!closed) {
					closed = true
					try {
						await writable.abort()
					} catch (error) {
						console.warn("Failed to abort writable stream", error)
					}
				}
			},
		}
	}

	const maybeCreateSyncAccess = (
		fileHandle as { createSyncAccessHandle?: CreateSyncAccessHandle }
	).createSyncAccessHandle

	const hasSyncAccess = typeof maybeCreateSyncAccess === "function"
	if (hasSyncAccess) {
		try {
			const accessHandle: OPFSSyncAccessHandle = await maybeCreateSyncAccess.call(fileHandle)

			let position = 0
			let closed = false

			const write = async (chunk: Uint8Array) => {
				const result = accessHandle.write(chunk, { at: position })
				await awaitMaybePromise(result)
				position += chunk.byteLength
			}

			const close = async () => {
				if (closed) {
					return
				}
				closed = true
				if (typeof accessHandle.truncate === "function") {
					await awaitMaybePromise(accessHandle.truncate(position))
				}
				if (typeof accessHandle.flush === "function") {
					await awaitMaybePromise(accessHandle.flush())
				}
				if (typeof accessHandle.close === "function") {
					await awaitMaybePromise(accessHandle.close())
				}
			}

			const abort = async () => {
				if (closed) {
					return
				}
				closed = true
				if (typeof accessHandle.close === "function") {
					await awaitMaybePromise(accessHandle.close())
				}
			}

			return {
				write,
				close,
				abort,
			}
		} catch (error) {
			console.warn("Failed to open sync access handle for OPFS file", error)
		}
	}

	try {
		return await createWorkerFileWriter(absolutePathSegments)
	} catch (error) {
		console.warn("Failed to create worker-based OPFS writer", error)
	}

	throw new Error("OPFS file handle does not support writable operations")
}

async function awaitMaybePromise<T>(value: T | Promise<T> | void): Promise<void> {
	if (value === undefined || value === null) {
		return
	}
	if (typeof value === "object" && typeof (value as Promise<unknown>).then === "function") {
		await (value as Promise<unknown>)
	}
}

async function createFileWritableStream(options: FilePathOptions) {
	const { root, relativePath, basePath = [] } = options
	// Wrap a file writer in a standard WritableStream so zip.js can stream data directly into OPFS.
	console.info("[SampleProject] createFileWritableStream", { relativePath, basePath })
	const writer = await createFileWriterForPath(root, relativePath, basePath)
	let closed = false

	const stream = new WritableStream<Uint8Array>({
		write: async (chunk) => {
			await writer.write(chunk)
		},
		close: async () => {
			if (!closed) {
				closed = true
				await writer.close()
			}
		},
		abort: async () => {
			if (!closed) {
				closed = true
				await safeAbortWriter(writer)
			}
		},
	})

	return {
		stream,
		ensureClosed: async () => {
			if (!closed) {
				closed = true
				await writer.close()
			}
		},
	}
}

function setStatus(status: SampleProjectStatus) {
	store.set(sampleProjectStatusAtom, status)
}

function getWriterWorker(): Worker {
	if (writerWorker) {
		return writerWorker
	}

	if (typeof window === "undefined" || typeof Worker === "undefined") {
		throw new Error("File writer worker is not available in this environment")
	}

	writerWorker = new Worker(new URL("./sample-project-writer.worker.ts", import.meta.url), {
		type: "module",
	})

	writerWorker.onmessage = (event: MessageEvent<WorkerResponseMessage>) => {
		const { requestId, status } = event.data
		const pending = writerWorkerPendingRequests.get(requestId)
		if (!pending) {
			return
		}
		writerWorkerPendingRequests.delete(requestId)

		if (status === "ok") {
			pending.resolve(event.data.writerId)
		} else {
			const info = event.data.error
			const error = new Error(info?.message ?? "Worker file writer error")
			error.name = info?.name ?? "Error"
			pending.reject(error)
		}
	}

	writerWorker.onerror = (event) => {
		const error =
			event.error instanceof Error
				? event.error
				: event.message
					? new Error(event.message)
					: new Error("File writer worker failed")
		writerWorkerPendingRequests.forEach(({ reject }) => reject(error))
		writerWorkerPendingRequests.clear()
	}

	return writerWorker
}

async function callWriterWorker<T>(
	type: WorkerCommandType,
	payload: Record<string, unknown>,
	transfer: Transferable[] = [],
): Promise<T> {
	const worker = getWriterWorker()
	const requestId = ++writerWorkerRequestId

	return new Promise<T>((resolve, reject) => {
		writerWorkerPendingRequests.set(requestId, {
			resolve: (value) => resolve(value as T),
			reject: (error) => reject(error),
		})
		worker.postMessage({ requestId, type, ...payload }, transfer)
	})
}

async function createWorkerFileWriter(pathSegments: string[]): Promise<FileWriter> {
	const writerId = await callWriterWorker<number>("open", {
		pathSegments: [...pathSegments],
	})

	return {
		write: async (chunk: Uint8Array) => {
			const transferableChunk = chunk.slice()
			await callWriterWorker<void>(
				"write",
				{
					writerId,
					chunk: transferableChunk.buffer,
				},
				[transferableChunk.buffer],
			)
		},
		close: async () => {
			await callWriterWorker<void>("close", { writerId })
		},
		abort: async () => {
			try {
				await callWriterWorker<void>("abort", { writerId })
			} catch (error) {
				console.warn("Failed to abort worker file writer", error)
			}
		},
	}
}

async function getFileHandleFromSegments(
	root: FileSystemDirectoryHandle,
	segments: string[],
): Promise<FileSystemFileHandle> {
	if (!Array.isArray(segments) || segments.length === 0) {
		throw new Error("Cannot resolve file handle without path segments")
	}

	const pathSegments = [...segments]
	const fileName = pathSegments.pop()
	if (!fileName) {
		throw new Error("Invalid file path")
	}

	let directory = root
	for (const segment of pathSegments) {
		directory = await directory.getDirectoryHandle(segment, { create: true })
	}

	return directory.getFileHandle(fileName, { create: true })
}
